import React from 'react';
import { Auto, Biking, Bus, Walking } from './icons';
import { defineMessage, FormattedMessage, MessageDescriptor } from 'react-intl';
import { Typography } from '@eventbrite/marmalade';
import { GACodes } from '@/components/_shared/constants';
import { LISTING_HEAP_IDS } from '@/components/_shared/constants';
import { TravelModeLink } from './TravelModeLink';
import { useIntl } from 'react-intl';
import styles from './HowToGetThere.module.scss';
import { Icon } from '@eventbrite/marmalade';

interface Props {
  location: { latitude: number; longitude: number };
}

export const HowToGetThere: React.FC<Props> = ({ location }) => {
  const { formatMessage } = useIntl();

  return (
    <div className={styles.directions}>
      <div className={styles.title}>
        <Typography as="h4">
          <FormattedMessage id="How do you want to get there?" />
        </Typography>
      </div>
      <ul className={styles.list}>
        {TRAVEL_MODE_LINKS.map((link) => (
          <li key={link.mode}>
            <TravelModeLink
              queryParams={link.queryParams}
              label={link.label}
              heapId={link.heapId}
              location={location}
              ariaLabel={formatMessage(link.ariaLabel)}
            >
              <Icon size="small" className={styles.icon}>
                {link.icon}
              </Icon>
              <Typography as="span" className={styles.text}>
                {formatMessage(link.text)}
              </Typography>
            </TravelModeLink>
          </li>
        ))}
      </ul>
    </div>
  );
};

const TRAVEL_MODE_LINKS: {
  mode: string;
  queryParams: Record<string, string>;
  label: string;
  heapId: string;
  ariaLabel: MessageDescriptor;
  icon: React.ReactElement;
  text: MessageDescriptor;
}[] = [
  {
    mode: 'driving',
    queryParams: { mode: 'driving' },
    label: GACodes.GA_LABEL_DRIVE,
    heapId: LISTING_HEAP_IDS.MAP_DRIVE,
    ariaLabel: defineMessage({ id: 'Driving directions', defaultMessage: 'Driving directions' }),
    icon: <Auto />,
    text: defineMessage({ id: 'Driving', defaultMessage: 'Driving' }),
  },
  {
    mode: 'transit',
    queryParams: { mode: 'transit', dirflg: 'r' },
    label: GACodes.GA_LABEL_BUS,
    heapId: LISTING_HEAP_IDS.MAP_BUS,
    ariaLabel: defineMessage({ id: 'Transit directions', defaultMessage: 'Transit directions' }),
    icon: <Bus />,
    text: defineMessage({ id: 'Public transport', defaultMessage: 'Public transport' }),
  },
  {
    mode: 'bicycling',
    queryParams: { mode: 'bicycling', dirflg: 'b' },
    label: GACodes.GA_LABEL_BIKE,
    heapId: LISTING_HEAP_IDS.MAP_BIKE,
    ariaLabel: defineMessage({ id: 'Cycling directions', defaultMessage: 'Cycling directions' }),
    icon: <Biking />,
    text: defineMessage({ id: 'Biking', defaultMessage: 'Biking' }),
  },
  {
    mode: 'walking',
    queryParams: { mode: 'walking', dirflg: 'w' },
    label: GACodes.GA_LABEL_WALK,
    heapId: LISTING_HEAP_IDS.MAP_WALK,
    ariaLabel: defineMessage({ id: 'Walking directions', defaultMessage: 'Walking directions' }),
    icon: <Walking />,
    text: defineMessage({
      id: 'Walking',
      defaultMessage: 'Walking',
      description: 'Transportation mode label for walking directions',
    }),
  },
];
